import { alpha, createTheme, darken } from '@material-ui/core';
import { green, red, yellow } from '@material-ui/core/colors';
import createSpacing from '@material-ui/core/styles/createSpacing';
import { Overrides } from '@material-ui/core/styles/overrides';

const TEXAS_NAVY = '#242E3F';
const AWBD_BLUE = '#223360';
const PADRE_BLUE = '#1750A4';
const ALAMO_GREY = '#656770';
const RON_WHITE = '#E0E6EF';
const AWBD_RED = '#B6242A';
const BATTLE_RED = '#D23037';
const CAPTION_TEXT = '#223162';

const theme = createTheme({
  overrides: {
    MuiAutocomplete: {
      paper: {
        backgroundColor: '#FFFFFF'
      }
    },
    MuiAccordionSummary: {
      root: {
        height: 40,
        minHeight: 40
      }
    },
    MuiAvatar: {
      root: {
        fontSize: '1rem'
      }
    },
    MuiCssBaseline: {
      '@global': {
        html: {
          fontSize: 16
        },
        body: {
          'overscroll-behavior-y': 'none',
          fontSize: '1rem'
        }
      }
    },
    MuiButton: {
      root: {
        borderRadius: 4
      }
    },
    MuiCard: {
      root: {
        borderRadius: 4
      }
    },
    MuiChip: {
      root: {
        backgroundColor: RON_WHITE,
        color: TEXAS_NAVY
      },
      clickable: {
        '&:hover, &:focus': {
          backgroundColor: '#CBD0D8' // Color specifically requested by Gabe (AWBD)
        }
      }
    },
    MuiContainer: {
      root: {
        paddingLeft: '24px !important', // AWBD site is 10px
        paddingRight: '24px !important'
      }
    },
    MuiLink: {
      root: {
        color: PADRE_BLUE,
        fontWeight: 900
      }
    },
    MuiListItemIcon: {
      root: { minWidth: 48 }
    },
    MuiMenu: {
      paper: { backgroundColor: '#FFFFFF' }
    },
    MuiTooltip: {
      tooltip: {
        padding: createSpacing(6)(1, 1.5),
        fontSize: `${12 / 16}rem`
      }
    },
    MuiRadio: {
      colorSecondary: {
        '&$checked + span': {
          color: 'rgb(182, 36, 42)'
        }
      }
    },
    MuiSwitch: {
      track: {
        opacity: 1,
        backgroundColor: darken(RON_WHITE, 0.2)
      }
    }
  } as Overrides,
  props: {
    MuiButton: {
      disableElevation: true
    },
    MuiContainer: {
      maxWidth: 'lg'
    },
    MuiLink: {
      underline: 'always'
    },
    MuiMenu: {
      elevation: 2
    },
    MuiSvgIcon: {
      fontSize: 'small'
    },
    MuiTooltip: {
      enterDelay: 700
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 800,
      lg: 1168, // AWBD site uses 1140 max-width for containers
      xl: 1920
    }
  },
  palette: {
    background: {
      paper: RON_WHITE,
      default: '#FFFFFF'
    },
    action: {
      hover: alpha(RON_WHITE, 0.3)
    },
    primary: {
      dark: TEXAS_NAVY,
      main: AWBD_BLUE,
      light: PADRE_BLUE,
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: AWBD_RED,
      light: BATTLE_RED,
      contrastText: '#FFFFFF'
    },
    text: {
      primary: TEXAS_NAVY,
      secondary: ALAMO_GREY
    },
    divider: darken(RON_WHITE, 0.08),
    success: {
      light: green['300'],
      main: green['700'],
      dark: green['800'],
      contrastText: '#FFFFFF'
    },
    info: {
      light: yellow['300'],
      main: yellow['700'],
      dark: yellow['900'],
      contrastText: '#FFFFFF'
    },
    error: {
      light: red['300'],
      main: red['700'],
      dark: red['900'],
      contrastText: '#FFFFFF'
    }
  },
  shape: {
    borderRadius: 2
  },
  typography: {
    fontFamily: ['Roboto', 'sans-serif'].join(','),
    htmlFontSize: 16,
    fontSize: 14,
    h1: {
      fontFamily: ['Jost', 'sans-serif'].join(','),
      fontSize: `${42 / 16}rem`,
      fontWeight: 900,
      lineHeight: 56 / 42,
      color: AWBD_BLUE
    },
    h2: {
      fontSize: `${32 / 16}rem`,
      fontWeight: 700,
      lineHeight: 42 / 32,
      color: AWBD_BLUE
    },
    h3: {
      fontFamily: ['Jost', 'sans-serif'].join(','),
      fontSize: `${24 / 16}rem`,
      fontWeight: 500,
      lineHeight: 30 / 24,
      color: AWBD_BLUE
    },
    h4: {
      fontSize: `${18 / 16}rem`,
      fontWeight: 500,
      lineHeight: 30 / 18,
      color: PADRE_BLUE
    },
    /** Section heading on style guide (theme.stories.tsx) */
    h5: {
      fontFamily: ['Jost', 'sans-serif'].join(','),
      fontSize: `${20 / 16}rem`,
      fontWeight: 400,
      lineHeight: 24 / 20,
      color: TEXAS_NAVY
    },
    /** Header menu nav buttons */
    h6: {
      fontFamily: ['Jost', 'sans-serif'].join(','),
      fontSize: `${14 / 16}rem`,
      fontWeight: 600,
      lineHeight: 24 / 20,
      textTransform: 'uppercase'
    },
    /** [Style Guide] Headshot name */
    subtitle1: {
      fontFamily: ['Jost', 'sans-serif'].join(','),
      fontSize: `${16 / 16}rem`,
      fontWeight: 500,
      lineHeight: 24 / 16,
      color: AWBD_BLUE
    },
    /** [Style Guide] Headshot title */
    subtitle2: {
      fontSize: `${14 / 16}rem`,
      fontWeight: 400,
      lineHeight: 20 / 14,
      color: PADRE_BLUE
    },
    body1: {
      fontSize: `${14 / 16}rem`,
      lineHeight: 20 / 14
    },
    /** [Style Guide] Headshot company */
    body2: {
      fontSize: `${13 / 16}rem`,
      lineHeight: 20 / 13,
      color: ALAMO_GREY
    },
    button: {
      fontSize: `${13 / 16}rem`,
      lineHeight: 20 / 13
    },
    caption: {
      fontSize: `${11 / 16}rem`,
      lineHeight: 15 / 11,
      color: CAPTION_TEXT
    }
  }
});

export default theme;
